// ---------------------------------------------------------------------------
// useDragSelect
//
// Generic "snapshot item geometry once at drag-start, resolve pointer
// position via cached rects on every mousemove (zero further DOM reads/
// forced reflows), commit a range on mouseup" engine.
//
// This is THE single biggest duplication in the original codebase:
// PlanogramVisual had ~140 lines of this for grid cells (cellRectsRef,
// computeDragPreviewRect, startDrag, the isDraggingSelection effect) and
// PlanogramTabular had a near-identical ~120 lines for table rows
// (tableRowRectsRef, computeRowDragPreviewRect, startRowDrag, the
// isDraggingRows effect). Both existed for the exact same reason (the
// elementFromPoint-forces-layout problem documented in PlanogramVisual's
// comments) and both reached the same solution independently — they're
// now ONE implementation, generic over how an "item" maps to a key.
//
// Usage: the consumer provides a way to (a) read all currently-rendered
// item elements + their keys at drag-start, and (b) compute the
// contiguous "range" between two keys (a rectangle for the grid, a
// contiguous run of visible rows for the table). Everything else —
// rect caching, pointer-to-key resolution, preview-rect math, the
// window-level mousemove/mouseup listeners — is shared.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface DragPreviewRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface UseDragSelectOptions<TKey extends string> {
    /** Returns every currently-rendered (key, DOMRect) pair, read exactly once at drag-start. */
    captureItemRects: () => Map<TKey, DOMRect>;
    /** Returns the ordered list of currently-visible keys, used to resolve a contiguous "range" between two keys (table rows need this; grid cells compute ranges purely from row/col math instead — see resolveRange). */
    captureVisibleOrder?: () => TKey[];
    /** Resolves which key (if any) the pointer at (x, y) is over, using only the cached rects — no DOM reads. */
    resolveKeyAtPoint: (rects: Map<TKey, DOMRect>, x: number, y: number) => TKey | null;
    /** Computes the full set of keys spanned between startKey and endKey (inclusive). */
    resolveRange: (startKey: TKey, endKey: TKey, visibleOrder: TKey[]) => TKey[];
    /** Computes the on-screen preview rectangle (relative to the scroll container) spanning startKey..endKey, using the cached rects. */
    computePreviewRect: (rects: Map<TKey, DOMRect>, startKey: TKey, endKey: TKey) => DragPreviewRect | null;
    /** Called once the drag ends with the full resolved range — copy-mode and selection-mode branch on this in the caller. */
    onCommit: (keys: TKey[]) => void;
}

export function useDragSelect<TKey extends string>(options: UseDragSelectOptions<TKey>) {
    const [isDragging, setIsDragging] = useState(false);
    const [hoverKeys, setHoverKeys] = useState<Set<TKey>>(new Set());
    const [previewRect, setPreviewRect] = useState<DragPreviewRect | null>(null);

    const rectsRef = useRef<Map<TKey, DOMRect>>(new Map());
    const visibleOrderRef = useRef<TKey[]>([]);
    const startKeyRef = useRef<TKey | null>(null);
    const endKeyRef = useRef<TKey | null>(null);
    const wasDraggingRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const startDrag = useCallback((key: TKey) => {
        const rects = optionsRef.current.captureItemRects();
        rectsRef.current = rects;
        visibleOrderRef.current = optionsRef.current.captureVisibleOrder?.() ?? Array.from(rects.keys());

        startKeyRef.current = key;
        endKeyRef.current = key;
        setIsDragging(true);
        setHoverKeys(new Set([key]));
        setPreviewRect(optionsRef.current.computePreviewRect(rects, key, key));
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (event: MouseEvent) => {
            const resolved = optionsRef.current.resolveKeyAtPoint(rectsRef.current, event.clientX, event.clientY);
            if (!resolved) return;
            if (endKeyRef.current === resolved) return;

            endKeyRef.current = resolved;
            const startKey = startKeyRef.current;
            if (!startKey) return;

            const range = optionsRef.current.resolveRange(startKey, resolved, visibleOrderRef.current);
            setHoverKeys(new Set(range));
            setPreviewRect(optionsRef.current.computePreviewRect(rectsRef.current, startKey, resolved));
        };

        const finishDrag = () => {
            const startKey = startKeyRef.current;
            const endKey = endKeyRef.current;

            if (startKey && endKey) {
                const range = optionsRef.current.resolveRange(startKey, endKey, visibleOrderRef.current);
                optionsRef.current.onCommit(range);

                // Only treat this as "a drag happened" (and therefore swallow
                // the click that mouseup is about to produce) if the pointer
                // actually moved across more than one item. A zero-movement
                // mousedown+mouseup — i.e. a plain click — must NOT set this,
                // or callers that allow startDrag() from a cold/no-mode state
                // (see PlanogramVisual/PlanogramTabular's cold-start drag) would
                // have every ordinary click silently swallowed by
                // consumeWasDragging() on the very next click event.
                if (range.length > 1) {
                    wasDraggingRef.current = true;
                }
            }

            startKeyRef.current = null;
            endKeyRef.current = null;
            rectsRef.current = new Map();
            visibleOrderRef.current = [];
            setIsDragging(false);
            setHoverKeys(new Set());
            setPreviewRect(null);
        };

        // Listen on `window`, not the container — the drag must keep
        // tracking even if the pointer briefly leaves the grid/table
        // bounds, and mouseup must always be caught regardless of what
        // element is under the cursor at release time.
        window.addEventListener('mousemove', handlePointerMove);
        window.addEventListener('mouseup', finishDrag);

        return () => {
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('mouseup', finishDrag);
        };
    }, [isDragging]);

    /** Call when the feature that owns this drag (selection mode / copy mode) is turned off elsewhere, to avoid stale state surviving into the next session. True no-op if no drag is in progress — does not touch state at all in that case, so it's always safe to call from an effect on every render without risking a setState loop. */
    const resetIfInactive = useCallback((isActive: boolean) => {
        if (isActive) return;
        if (startKeyRef.current === null && !isDragging) return; // nothing to reset
        startKeyRef.current = null;
        endKeyRef.current = null;
        rectsRef.current = new Map();
        visibleOrderRef.current = [];
        setIsDragging(false);
        setHoverKeys(new Set());
        setPreviewRect(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    /** Consumed by the click handler on the same element that started the drag, to swallow the synthetic click mouseup produces. */
    const consumeWasDragging = useCallback((): boolean => {
        if (!wasDraggingRef.current) return false;
        wasDraggingRef.current = false;
        return true;
    }, []);

    // *** FIX: Maximum update depth exceeded ***
    //
    // This used to return a fresh object literal on every render. startDrag
    // and consumeWasDragging are stable ([] deps), but resetIfInactive now
    // depends on isDragging (see its own comment above — it needs to read
    // the latest isDragging to decide whether there's anything to reset),
    // so its identity changes alongside isDragging. Since isDragging is
    // already in this memo's dependency list, that's fine: both update
    // together in the same render pass, and the memoized object's identity
    // is otherwise stable across renders where nothing here actually changed.
    //
    // Without this memo, ANY consumer that puts the whole returned object
    // in a dependency array (e.g.
    // `useEffect(() => dragSelect.resetIfInactive(x), [x, dragSelect])`)
    // would see a "changed" dependency on every render regardless of
    // whether anything meaningful changed — which is exactly the loop
    // PlanogramTabular hit: a new dragSelect object every render kept
    // re-triggering that effect, which (before the resetIfInactive no-op
    // fix above) kept calling state setters, which kept causing re-renders.
    return useMemo(() => ({
        isDragging,
        hoverKeys,
        previewRect,
        startDrag,
        resetIfInactive,
        consumeWasDragging,
    }), [isDragging, hoverKeys, previewRect, startDrag, resetIfInactive, consumeWasDragging]);
}

export type UseDragSelectReturn<TKey extends string> = ReturnType<typeof useDragSelect<TKey>>;

// ---------------------------------------------------------------------------
// Ready-made resolvers for the two concrete shapes used by Visual/Tabular.
// Kept here (not duplicated per-component) since the math is identical to
// what was inlined in each component before.
// ---------------------------------------------------------------------------

/** Grid-cell variant: keys are "row-col", range = full rectangle between two cells. */
export const gridRangeResolvers = {
    resolveKeyAtPoint: (rects: Map<string, DOMRect>, x: number, y: number): string | null => {
        for (const [key, rect] of rects) {
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                return key;
            }
        }
        return null;
    },

    resolveRange: (startKey: string, endKey: string): string[] => {
        const [startRow, startCol] = startKey.split('-').map(Number);
        const [endRow, endCol] = endKey.split('-').map(Number);

        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);
        const minCol = Math.min(startCol, endCol);
        const maxCol = Math.max(startCol, endCol);

        const keys: string[] = [];
        for (let row = minRow; row <= maxRow; row++) {
            for (let col = minCol; col <= maxCol; col++) {
                keys.push(`${row}-${col}`);
            }
        }
        return keys;
    },

    computePreviewRect: (
        rects: Map<string, DOMRect>,
        startKey: string,
        endKey: string,
        container: HTMLElement | null,
    ): DragPreviewRect | null => {
        if (!container) return null;

        const [startRow, startCol] = startKey.split('-').map(Number);
        const [endRow, endCol] = endKey.split('-').map(Number);
        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);
        const minCol = Math.min(startCol, endCol);
        const maxCol = Math.max(startCol, endCol);

        const topLeftRect = rects.get(`${minRow}-${minCol}`);
        const bottomRightRect = rects.get(`${maxRow}-${maxCol}`);
        if (!topLeftRect || !bottomRightRect) return null;

        const containerRect = container.getBoundingClientRect();

        return {
            top: topLeftRect.top - containerRect.top + container.scrollTop,
            left: topLeftRect.left - containerRect.left + container.scrollLeft,
            width: bottomRightRect.right - topLeftRect.left,
            height: bottomRightRect.bottom - topLeftRect.top,
        };
    },
};

/** Table-row variant: keys are "row-col" (one per visible <tr>), range = contiguous run in visible display order. */
export const rowRangeResolvers = {
    resolveKeyAtPoint: (rects: Map<string, DOMRect>, _x: number, y: number): string | null => {
        for (const [key, rect] of rects) {
            if (y >= rect.top && y <= rect.bottom) {
                return key;
            }
        }
        return null;
    },

    resolveRange: (startKey: string, endKey: string, visibleOrder: string[]): string[] => {
        const startPos = visibleOrder.indexOf(startKey);
        const endPos = visibleOrder.indexOf(endKey);
        if (startPos === -1 || endPos === -1) return [startKey];

        const from = Math.min(startPos, endPos);
        const to = Math.max(startPos, endPos);
        return visibleOrder.slice(from, to + 1);
    },

    computePreviewRect: (
        rects: Map<string, DOMRect>,
        startKey: string,
        endKey: string,
        container: HTMLElement | null,
    ): DragPreviewRect | null => {
        const startRect = rects.get(startKey);
        const endRect = rects.get(endKey);
        if (!container || !startRect || !endRect) return null;

        const containerRect = container.getBoundingClientRect();
        const top = Math.min(startRect.top, endRect.top) - containerRect.top + container.scrollTop;
        const left = Math.min(startRect.left, endRect.left) - containerRect.left + container.scrollLeft;
        const right = Math.max(startRect.right, endRect.right) - containerRect.left + container.scrollLeft;
        const bottom = Math.max(startRect.bottom, endRect.bottom) - containerRect.top + container.scrollTop;

        return { top, left, width: right - left, height: bottom - top };
    },
};